import React, { useCallback, useRef, useState } from "react";
import { GridColDef, GridRenderCellParams, GridRowId, useGridApiContext } from "@mui/x-data-grid-premium";
import { Typography, Popper, Paper, InputBase, InputBaseProps } from "@mui/material";
import { MirageTheme } from "@mirage/theme/MuiMirage.module";

type CellWithPopoverSubmit = (id: GridRowId, field: string, newVal: any) => Promise<void>;
interface CellWithPopoverProps {
    onSubmit?: CellWithPopoverSubmit;
    editedOutside?: ((id: GridRowId) => boolean) | boolean;
}

type CellWithPopoverReturn = Pick<GridColDef, "renderCell" | "renderEditCell" | "editable">;

const cellWithPopover = (onSubmit?: CellWithPopoverSubmit, editedOutside?: ((id: GridRowId) => boolean) | boolean): CellWithPopoverReturn => {
    return {
        renderCell: (p) => <CellWithPopoverView {...p} editedOutside={editedOutside} />,
        renderEditCell: (p) => <CellWithPopoverEdit {...p} onSubmit={onSubmit} editedOutside={editedOutside} />,
        editable: true,
    } as GridColDef;
};

const CellWithPopoverEdit: React.FC<CellWithPopoverProps & GridRenderCellParams> = ({ onSubmit, editedOutside: editedOutsideProps, ...params }) => {
    const { value, colDef, id, field, cellMode } = params;
    const isEdit = cellMode === "edit";
    const apiRef = useGridApiContext();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [currentVal, setCurrentVal] = useState(value || "");
    const valueUpdated = useRef(false);
    const editedOutside = typeof editedOutsideProps === "boolean" ? editedOutsideProps : editedOutsideProps && editedOutsideProps(id);
    const valueLength = currentVal?.length || 0;
    const cellElement = apiRef.current.getCellElement(id, field);
    const cellElementPaddingTopWithPx = cellElement ? window.getComputedStyle(cellElement, null).getPropertyValue("padding-top") : "6px";
    const cellElementPaddingLeftWithPx = cellElement ? window.getComputedStyle(cellElement, null).getPropertyValue("padding-left") : "10px";
    const cellElementPaddingTop = Number(cellElementPaddingTopWithPx.replace("px", ""));
    const cellElementPaddingLeft = Number(cellElementPaddingLeftWithPx.replace("px", ""));
    const handleRef = React.useCallback((el: HTMLElement | null) => {
        setAnchorEl(el);
    }, []);
    const onInputFocus = useCallback(
        (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement, Element>) => {
            e.currentTarget.setSelectionRange(valueLength, valueLength);
        },
        [valueLength]
    );
    const open = Boolean(anchorEl);
    const handleBlur = async () => {
        if (!editedOutside) {
            isEdit && apiRef.current.stopCellEditMode({ id, field });
            if (onSubmit && valueUpdated.current) {
                await onSubmit(id, field, currentVal);
            }
        }
    };

    const handleChange = React.useCallback<NonNullable<InputBaseProps["onChange"]>>(
        (event) => {
            valueUpdated.current = true;
            const newValue = event.target.value;
            setCurrentVal(newValue);
            apiRef.current.setEditCellValue({ id, field, value: newValue, debounceMs: 200 }, event);
        },
        [apiRef, field, id]
    );

    return (
        <div style={{ position: "relative", alignSelf: "flex-start", overflow: "hidden" }} onBlur={handleBlur}>
            <div
                ref={handleRef}
                style={{
                    height: 1,
                    width: colDef.computedWidth,
                    display: "block",
                    position: "absolute",
                    top: -cellElementPaddingTop,
                    left: -cellElementPaddingLeft,
                }}
            />
            <div>{currentVal}</div>
            <Popper
                sx={{
                    pointerEvents: "all",
                }}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start"
            >
                <Paper elevation={1} sx={{ p: 1, minWidth: colDef.computedWidth }}>
                    <InputBase
                        autoFocus
                        multiline
                        rows={4}
                        value={currentVal}
                        sx={{ textarea: { resize: "both" }, width: "100%" }}
                        onChange={handleChange}
                        onFocus={onInputFocus}
                    />
                </Paper>
            </Popper>
        </div>
    );
};

const CellWithPopoverView: React.FC<Pick<CellWithPopoverProps, "editedOutside"> & GridRenderCellParams> = ({ editedOutside: editedOutsideProps, ...params }) => {
    const { value, colDef, id, field } = params;
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const anchorRef = useRef<HTMLElement | null>(null);
    const editedOutside = typeof editedOutsideProps === "boolean" ? editedOutsideProps : editedOutsideProps && editedOutsideProps(id);
    const apiRef = useGridApiContext();
    const cellHeight = MirageTheme.components?.MuiDataGrid?.defaultProps?.rowHeight || 40;
    const cellElement = apiRef.current.getCellElement(id, field);
    const cellElementPaddingTopWithPx = cellElement ? window.getComputedStyle(cellElement, null).getPropertyValue("padding-top") : "6px";
    const cellElementPaddingTop = Number(cellElementPaddingTopWithPx.replace("px", ""));
    const handlePopoverOpen = () => {
        setAnchorEl(anchorRef.current);
    };
    const handlePopoverClose = () => {
        setAnchorEl(null);
    };
    const onCellClick = () => {
        const rowIds = apiRef.current.getAllRowIds();
        rowIds.forEach((rowId) => {
            if (rowId !== id) {
                const cellMode = apiRef.current.getCellMode(rowId, field);
                cellMode === "edit" && apiRef.current.stopCellEditMode({ id: rowId, field });
            }
        });
        handlePopoverOpen();
        if (!editedOutside) {
            apiRef.current.startCellEditMode({ id, field });
        }
    };

    const handleRef = React.useCallback((el: HTMLElement | null) => {
        anchorRef.current = el;
    }, []);
    const open = Boolean(anchorEl);
    return (
        <div
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            style={{ display: "flex", flexBasis: "100%", minHeight: "100%", position: "relative", alignSelf: "center", alignItems: "center", overflow: "hidden" }}
            onClick={onCellClick}
        >
            <div
                ref={handleRef}
                style={{
                    minHeight: cellHeight,
                    width: colDef.computedWidth,
                    display: "block",
                    position: "absolute",
                    top: -cellElementPaddingTop,
                }}
            />
            <div>{value}</div>
            <Popper
                sx={{
                    pointerEvents: "none",
                }}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-end"
            >
                <Paper
                    elevation={1}
                    sx={{
                        p: 1,
                        minWidth: colDef.computedWidth,
                        maxWidth: colDef.computedWidth,
                        minHeight: cellHeight,
                        transform: `translate(-10px, -${cellHeight}px)`,
                    }}
                >
                    <Typography sx={{ width: "100%", wordWrap: "break-word", whiteSpace: "pre-wrap" }}>{value}</Typography>
                </Paper>
            </Popper>
        </div>
    );
};

export default cellWithPopover;
